// lib/machineRecordsApi.js
// R7 머신 기록 관리자 API. 계약: docs/architecture/app-v2/R6b-R7.md "# R7" 3.2·3.3과 11절 구현 기준(API 계약에 더한 것)
//   GET  /v1/admin/classes/{class_id}/machine-records
//        → { class_id, bookings: [{ booking_id, workout_id(null 가능), user_id, name, gender("male"|"female"|null),
//              status("confirmed"|"cancelled"), attended, records: [{ machine, metric, value }] }] }
//              (출석 처리된 회원 예약은 기록 없어도 행, 게스트 제외. 그 뒤에 확정·출석이 아닌데 기록이 남은 예약 = 지우기 전용 행)
//   POST /v1/admin/bookings/{booking_id}/machine-records   { records: [{ machine, metric, value(null = 삭제) }] }
//        → 200 { booking_id, workout_id, saved, deleted, records, pr: [{ machine, metric, previous_best, value }],
//                grade_changes: [{ machine, from, to, label }] }
//        → 400 { error: { code, message, details: { errors: [{ index, machine, metric, message }] } } } (하나라도 틀리면 전부 거절.
//              요청 자체가 잘못됐으면 details 없음), 404 예약 없음, 409 출석 처리 안 된 예약(지우기만 하는 요청은 받음), 422 게스트 예약
// api.js의 api/apiJson은 실패 응답마다 전역 에러 팝업을 띄우고 본문을 읽어 버린다. 이 화면은 예약(행)마다 오류 문구와
// 칸별 오류(details.errors)를 보여야 해서 API 주소 규칙은 api.js와 같게 두고 직접 fetch한다(쿠키 동봉, lib/rewardsApi.js와 같은 방식).
// 이 파일은 import가 없다(scripts/test-machine-records.mjs가 node로 그대로 불러 시험한다).

const API_BASE =
  (typeof window !== "undefined" && window.__APP_CONFIG__?.API_BASE) ||
  process.env.VUE_APP_API_BASE || // Vue CLI
  ""; // 같은 오리진이면 빈 문자열

// status 0 = 응답을 받지 못함(네트워크 오류). serverMessage는 서버가 준 문구(있으면), code는 서버 오류 코드,
// itemErrors는 400의 details.errors(항목별 오류, 없으면 [])
export class MachineRecordsApiError extends Error {
  constructor(status, serverMessage = "", code = "", itemErrors = []) {
    super(serverMessage || (status ? `HTTP ${status}` : "NETWORK"));
    this.name = "MachineRecordsApiError";
    this.status = status;
    this.serverMessage = serverMessage;
    this.code = code;
    this.itemErrors = itemErrors;
  }
}

// 실패 응답 본문에서 문구·코드·항목별 오류를 꺼낸다.
// {"success":false,"error":{"code","message","details":{"errors":[…]}}} / {"error":"…"} / {"message":"…"} / 일반 텍스트
export async function readErrorBody(res) {
  let text = "";
  try {
    text = await res.text();
  } catch {
    return { message: "", code: "", itemErrors: [] };
  }
  try {
    const data = JSON.parse(text);
    if (data?.error && typeof data.error === "object") {
      const errors = data.error.details?.errors;
      return {
        message: String(data.error.message || ""),
        code: String(data.error.code || ""),
        itemErrors: Array.isArray(errors)
          ? errors
              .filter((e) => e && typeof e === "object")
              .map((e) => ({
                index: Number.isInteger(e.index) ? e.index : null,
                machine: typeof e.machine === "string" ? e.machine : "",
                metric: typeof e.metric === "string" ? e.metric : "",
                message: String(e.message || ""),
              }))
          : [],
      };
    }
    if (typeof data?.error === "string") {
      return { message: data.error, code: "", itemErrors: [] };
    }
    if (typeof data?.message === "string") {
      return { message: data.message, code: "", itemErrors: [] };
    }
  } catch {
    // JSON이 아니면 아래에서 텍스트 그대로
  }
  return { message: String(text || "").trim(), code: "", itemErrors: [] };
}

async function request(path, opts = {}) {
  let res;
  try {
    res = await fetch(API_BASE + path, {
      credentials: "include",
      ...opts,
      headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    });
  } catch {
    throw new MachineRecordsApiError(0);
  }
  if (!res.ok) {
    const { message, code, itemErrors } = await readErrorBody(res);
    throw new MachineRecordsApiError(res.status, message, code, itemErrors);
  }
  try {
    return await res.json();
  } catch {
    throw new MachineRecordsApiError(res.status, "", "BAD_JSON");
  }
}

function seg(value) {
  return encodeURIComponent(String(value == null ? "" : value));
}

// 서버 행 → 화면 행. booking_id가 없는 행은 버린다(저장할 곳이 없음).
// deleteOnly = 확정·출석이 아닌 예약(출석한 뒤 취소됨)의 남은 기록: 값을 비워 지우기만 할 수 있다(서버는 값이 있는 요청을 409로 거절).
// status·attended가 없는 응답(옛 서버)은 지우기 전용이 아니다
export function normalizeClassBookings(data) {
  const list = Array.isArray(data?.bookings) ? data.bookings : [];
  return list
    .filter((b) => b && typeof b === "object" && b.booking_id)
    .map((b) => ({
      bookingId: String(b.booking_id),
      workoutId: b.workout_id ? String(b.workout_id) : null,
      userId: b.user_id ? String(b.user_id) : "",
      name: String(b.name || "").trim() || "이름 없음",
      gender: b.gender === "male" || b.gender === "female" ? b.gender : null,
      deleteOnly:
        (typeof b.status === "string" && b.status !== "confirmed") ||
        b.attended === false,
      records: Array.isArray(b.records) ? b.records : [],
    }));
}

// 그 수업의 출석 처리된 회원 예약과 기존 기록(3.3). 실패는 MachineRecordsApiError
export async function fetchClassMachineRecords(classId) {
  const data = await request(
    `/v1/admin/classes/${seg(classId)}/machine-records`
  );
  return normalizeClassBookings(data);
}

// 한 예약의 기록 upsert·삭제(3.2). records = [{ machine, metric, value(null = 삭제) }]. 성공하면 응답 본문
export async function saveBookingMachineRecords(bookingId, records) {
  const data = await request(
    `/v1/admin/bookings/${seg(bookingId)}/machine-records`,
    { method: "POST", body: JSON.stringify({ records }) }
  );
  return {
    // 그 예약의 지금 기록 전부(없는 응답은 null: 폼이 보낸 칸만 반영, machineCatalog.applySaved)
    records: Array.isArray(data?.records) ? data.records : null,
    pr: Array.isArray(data?.pr) ? data.pr : [],
    gradeChanges: Array.isArray(data?.grade_changes) ? data.grade_changes : [],
    workoutId: data?.workout_id ? String(data.workout_id) : null,
  };
}

function isKorean(text) {
  return /[가-힣]/.test(String(text || ""));
}

// 저장 실패 → 행 옆 문구. 400 항목별 오류는 칸에도 따로 보인다(itemErrors)
export function describeSaveError(err) {
  const status = err?.status;
  const server = isKorean(err?.serverMessage) ? err.serverMessage : "";
  if (status === 400) {
    if (Array.isArray(err?.itemErrors) && err.itemErrors.length) {
      return "빨간 칸의 값을 고쳐 주세요. 이 회원의 기록은 저장하지 않았어요.";
    }
    return (
      server || "요청을 처리하지 못했어요. 새로고침한 뒤 다시 저장해 주세요."
    );
  }
  if (status === 404) {
    // 서버 문구(BOOKING_001 "예약을 찾을 수 없습니다")는 기존 관리자 API와 같은 합니다체라 쓰지 않고, 이 화면 말투와 다음 할 일을 보인다
    return "예약을 찾을 수 없어요. 목록을 다시 불러와 주세요.";
  }
  if (status === 409) {
    // 목록은 출석 처리된 예약만 값을 받으므로 409는 화면을 연 뒤 예약이 바뀐 경우다(다른 탭의 수업관리에서 취소 등). 서버 문구
    // (MACHINE_001 "출석 처리된 예약만 입력할 수 있어요")는 방금 출석으로 보인 행과 맞지 않고 다시 저장해도 같으므로, 404처럼
    // 이유와 다음 할 일을 보인다. 다시 불러오면 그 행은 "취소된 예약 · 지우기만 가능"이 된다(R7 가벼운 리뷰 4차)
    return "이 예약은 출석 처리된 상태가 아니에요(취소됐을 수 있어요). 목록을 다시 불러와 주세요.";
  }
  if (status === 422) {
    return server || "게스트 예약에는 머신 기록을 남길 수 없어요.";
  }
  if (status === 401) return "로그인이 만료됐어요. 다시 로그인해 주세요.";
  if (status === 403) return "관리자 계정만 머신 기록을 저장할 수 있어요.";
  if (status === 0) {
    // upsert라 같은 값을 다시 보내도 기록이 늘지 않는다
    return "서버 응답을 받지 못했어요. 다시 저장해 주세요.";
  }
  return `저장하지 못했어요 (오류 ${status}). 잠시 뒤 다시 시도해 주세요.`;
}

// 목록 불러오기 실패 문구
export function describeLoadError(err) {
  const status = err?.status;
  if (status === 401) return "로그인이 만료됐어요. 다시 로그인해 주세요.";
  if (status === 403) return "관리자 계정만 머신 기록을 볼 수 있어요.";
  if (status === 404) return "수업을 찾을 수 없어요. 삭제됐을 수 있어요.";
  if (status === 0) {
    return "서버에 연결하지 못했어요. 잠시 뒤 다시 불러와 주세요.";
  }
  return `머신 기록을 불러오지 못했어요 (오류 ${status}).`;
}
